/**
 * Theme cosmetic card/live previews (Shop, Inventory, Shop Creator).
 */
import {
  THEME_COSMETIC_CHANNEL_KEYS,
  THEME_COSMETIC_CHANNEL_LABELS,
  cssGradientFromChannel,
  themeCosmeticConfiguredKeys,
  type ThemeCosmeticChannel,
  type ThemeCosmeticChannelKey,
  type ThemeCosmeticData,
} from "./theme-cosmetic";
import { createModalCloseButton } from "./modal-close-button";

const BADGE_CYCLE_MS = 2000;

const EYE_ICON =
  '<svg class="themeCosmeticEyeIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 5c-5.5 0-9.7 4.1-11 7 1.3 2.9 5.5 7 11 7s9.7-4.1 11-7c-1.3-2.9-5.5-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>';

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mixRgb(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number,
) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/** Fill a canvas with only the Theme background channel (or dark plate if absent). */
export function paintThemeBackgroundPreview(
  canvas: HTMLCanvasElement,
  data: ThemeCosmeticData | null | undefined,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#121218";
  ctx.fillRect(0, 0, w, h);

  const bg = data?.background;
  if (!bg || !bg.colors.length) {
    ctx.fillStyle = "#1a1a22";
    ctx.fillRect(0, 0, w, h);
    return;
  }

  if (bg.mode === "gradient" && bg.colors.length > 1) {
    const angle = bg.angleDeg ?? 180;
    const rad = ((angle - 90) * Math.PI) / 180;
    const x1 = w / 2 - Math.cos(rad) * w;
    const y1 = h / 2 - Math.sin(rad) * h;
    const x2 = w / 2 + Math.cos(rad) * w;
    const y2 = h / 2 + Math.sin(rad) * h;
    const g = ctx.createLinearGradient(x1, y1, x2, y2);
    bg.colors.forEach((c, i) => {
      g.addColorStop(i / Math.max(1, bg.colors.length - 1), c);
    });
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = bg.colors[0];
  }
  ctx.fillRect(0, 0, w, h);
}

/** Cycle a single label every 2s. Returns stop(). */
export function startThemeChannelBadgeCycle(
  badge: HTMLElement,
  labels: string[],
): () => void {
  if (!labels.length) {
    badge.textContent = "";
    badge.classList.add("hidden");
    return () => {};
  }
  badge.classList.remove("hidden");
  let idx = 0;
  badge.textContent = labels[0];
  if (labels.length === 1) return () => {};

  const tick = () => {
    if (!badge.isConnected) {
      window.clearInterval(timer);
      return;
    }
    idx = (idx + 1) % labels.length;
    badge.textContent = labels[idx];
  };
  const timer = window.setInterval(tick, BADGE_CYCLE_MS);
  return () => window.clearInterval(timer);
}

function applyButtonsChannelCss(el: HTMLElement, ch: ThemeCosmeticChannel): void {
  const from = ch.colors[0] || "#66ffff";
  const to = ch.colors[ch.colors.length - 1] || from;
  el.style.setProperty("--home-start-glow", from);
  el.style.setProperty("--btn-main-glow", from);
  if (ch.mode === "gradient" && ch.colors.length > 1) {
    const a = hexToRgb(from);
    const b = hexToRgb(to);
    const mid = mixRgb(a, b, 0.45);
    const edge = mixRgb(b, { r: 0, g: 0, b: 0 }, 0.35);
    const edgeHi = mixRgb(b, { r: 0, g: 0, b: 0 }, 0.45);
    el.style.setProperty("--btn-sky-light", from);
    el.style.setProperty("--btn-sky-mid", rgbToHex(mid.r, mid.g, mid.b));
    el.style.setProperty("--btn-sky-edge", rgbToHex(edge.r, edge.g, edge.b));
    el.style.setProperty("--btn-sky-edge-hover", rgbToHex(edgeHi.r, edgeHi.g, edgeHi.b));
  } else {
    el.style.setProperty("--btn-sky-light", from);
    el.style.setProperty("--btn-sky-mid", from);
    el.style.setProperty("--btn-sky-edge", from);
    el.style.setProperty("--btn-sky-edge-hover", from);
  }
  const lum = (0.2126 * hexToRgb(from).r + 0.7152 * hexToRgb(from).g + 0.0722 * hexToRgb(from).b) / 255;
  el.style.setProperty("--btn-fg", lum > 0.55 ? "#000000" : "#ffffff");
}

function paintLiveBall(canvas: HTMLCanvasElement, ch: ThemeCosmeticChannel): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const rr = Math.min(w, h) * 0.32;
  const from = ch.colors[0] || "#66ffff";
  const to = ch.colors[ch.colors.length - 1] || from;

  // Soft neon glow (matches in-game neon balls look).
  ctx.save();
  ctx.shadowColor = from;
  ctx.shadowBlur = rr * 0.9;
  ctx.globalAlpha = 0.32;
  ctx.fillStyle = from;
  ctx.beginPath();
  ctx.arc(cx, cy, rr * 1.08, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.arc(cx, cy, rr, 0, Math.PI * 2);
  if (ch.mode === "gradient" && ch.colors.length > 1) {
    // Same diagonal gradient as Obstacle.draw in legacy-app.
    const g = ctx.createLinearGradient(cx - rr, cy - rr, cx + rr, cy + rr);
    if (ch.colors.length === 2) {
      g.addColorStop(0, from);
      g.addColorStop(1, to);
    } else {
      ch.colors.forEach((c, i) => g.addColorStop(i / Math.max(1, ch.colors.length - 1), c));
    }
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = from;
  }
  ctx.fill();
}

/** Modal: live look for each configured Theme channel. */
export function openThemeLivePreviewModal(data: ThemeCosmeticData): void {
  const keys = themeCosmeticConfiguredKeys(data);
  const hasParticles = !!data.decorations?.some((d) => d.kind === "particles");
  if (!keys.length && !hasParticles) return;

  const existing = document.querySelector(".themeCosmeticLiveModal");
  existing?.remove();

  const modal = document.createElement("div");
  modal.className = "themeCosmeticLiveModal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Theme preview");

  const panel = document.createElement("div");
  panel.className = "themeCosmeticLivePanel modeBox";

  const head = document.createElement("div");
  head.className = "themeCosmeticLiveHead";
  const title = document.createElement("h2");
  title.textContent = "Theme preview";
  head.appendChild(title);
  const closeBtn = createModalCloseButton(() => modal.remove(), "Close theme preview");
  head.appendChild(closeBtn);
  panel.appendChild(head);

  const list = document.createElement("div");
  list.className = "themeCosmeticLiveList";

  for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
    if (!keys.includes(key)) continue;
    const ch = data[key];
    if (!ch) continue;

    const row = document.createElement("section");
    row.className = "themeCosmeticLiveRow";
    const lab = document.createElement("h3");
    lab.textContent = THEME_COSMETIC_CHANNEL_LABELS[key];
    row.appendChild(lab);

    const stage = document.createElement("div");
    stage.className = "themeCosmeticLiveStage";

    if (key === "background") {
      stage.classList.add("themeCosmeticLiveStage--bg");
      stage.style.background = cssGradientFromChannel(ch);
    } else if (key === "balls") {
      const canvas = document.createElement("canvas");
      canvas.width = 160;
      canvas.height = 160;
      canvas.className = "themeCosmeticLiveBallCanvas";
      paintLiveBall(canvas, ch);
      stage.appendChild(canvas);
    } else if (key === "text") {
      const sample = document.createElement("p");
      sample.className = "themeCosmeticLiveTextSample";
      sample.textContent = "This is how it looks";
      if (ch.mode === "gradient" && ch.colors.length > 1) {
        sample.style.backgroundImage = cssGradientFromChannel(ch);
        sample.style.webkitBackgroundClip = "text";
        sample.style.backgroundClip = "text";
        sample.style.color = "transparent";
        (sample.style as CSSStyleDeclaration & { webkitTextFillColor?: string }).webkitTextFillColor =
          "transparent";
      } else {
        sample.style.color = ch.colors[0];
      }
      stage.appendChild(sample);
    } else if (key === "buttons") {
      const btnWrap = document.createElement("div");
      btnWrap.className = "themeCosmeticLiveStartWrap";
      applyButtonsChannelCss(btnWrap, ch);
      const startBtn = document.createElement("button");
      startBtn.type = "button";
      startBtn.className = "btn homeStartBtn themeCosmeticLiveStartBtn";
      startBtn.textContent = "START";
      startBtn.tabIndex = -1;
      btnWrap.appendChild(startBtn);
      stage.appendChild(btnWrap);
    }

    row.appendChild(stage);
    list.appendChild(row);
  }

  if (hasParticles) {
    const row = document.createElement("section");
    row.className = "themeCosmeticLiveRow";
    const lab = document.createElement("h3");
    lab.textContent = "Particles";
    row.appendChild(lab);
    const stage = document.createElement("div");
    stage.className = "themeCosmeticLiveStage";
    const img = document.createElement("img");
    img.className = "themeCosmeticLiveParticleThumb";
    img.alt = "Particle art";
    const dec = data.decorations!.find((d) => d.kind === "particles");
    if (dec?.imageData) img.src = dec.imageData;
    stage.appendChild(img);
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = "Use Preview to see particles in the game.";
    row.appendChild(stage);
    row.appendChild(hint);
    list.appendChild(row);
  }

  panel.appendChild(list);
  modal.appendChild(panel);
  document.body.appendChild(modal);

  modal.addEventListener("click", (ev) => {
    if (ev.target === modal) modal.remove();
  });
}

export function createThemeEyeButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "themeCosmeticEyeBtn";
  btn.setAttribute("aria-label", "Preview theme channels");
  btn.title = "Preview";
  btn.innerHTML = EYE_ICON;
  btn.onclick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    onClick();
  };
  return btn;
}

/** Card chrome: cycling label above preview + eye (top-left of card). */
export function attachThemeCardChrome(
  card: HTMLElement,
  previewWrap: HTMLElement,
  data: ThemeCosmeticData | null | undefined,
  opts?: { idleCanvas?: HTMLCanvasElement; hoverCanvas?: HTMLCanvasElement },
): void {
  const keys = themeCosmeticConfiguredKeys(data);
  const labels = [
    ...keys.map((k) => THEME_COSMETIC_CHANNEL_LABELS[k]),
    ...(data?.decorations?.some((d) => d.kind === "particles") ? ["Particles"] : []),
  ];
  card.classList.add("shopCosmeticCard--theme");

  if (opts?.idleCanvas) paintThemeBackgroundPreview(opts.idleCanvas, data);
  if (opts?.hoverCanvas) paintThemeBackgroundPreview(opts.hoverCanvas, data);

  const labelRow = document.createElement("div");
  labelRow.className = "themeCosmeticLabelRow";
  const badge = document.createElement("span");
  badge.className = "shopCosmeticTrailBadge shopCosmeticThemeBadge themeCosmeticCycleBadge";
  labelRow.appendChild(badge);
  // Insert label above the representation box.
  previewWrap.before(labelRow);
  const stopCycle = startThemeChannelBadgeCycle(badge, labels);

  if (data && labels.length) {
    const eye = createThemeEyeButton(() => openThemeLivePreviewModal(data));
    card.appendChild(eye);
  }

  const obs = new MutationObserver(() => {
    if (!card.isConnected) {
      stopCycle();
      obs.disconnect();
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
}

/** Floating bar while navigating the game with a Shop Creator Theme draft. */
export function mountThemeCosmeticPreviewChrome(opts: {
  getData: () => ThemeCosmeticData | null;
  setData: (data: ThemeCosmeticData | null) => void;
  onBack: () => void;
}): { destroy: () => void } {
  void opts.getData;
  void opts.setData;
  const bar = document.createElement("div");
  bar.id = "themeCosmeticPreviewChrome";
  bar.className = "themePreviewChrome";

  const dragHandle = document.createElement("span");
  dragHandle.className = "themePreviewChromeLabel themePreviewChromeDrag";
  dragHandle.textContent = "Theme Preview · records & Dots off";
  dragHandle.title = "Drag to move";
  bar.appendChild(dragHandle);

  try {
    const raw = localStorage.getItem("themeCosmeticPreviewChromePos");
    if (raw) {
      const pos = JSON.parse(raw) as { left?: number; top?: number };
      if (typeof pos.left === "number" && typeof pos.top === "number") {
        bar.style.left = `${pos.left}px`;
        bar.style.top = `${pos.top}px`;
        bar.style.transform = "none";
      }
    }
  } catch {
    /* ignore */
  }

  let drag: { ox: number; oy: number; sl: number; st: number } | null = null;
  dragHandle.onpointerdown = (ev) => {
    ev.preventDefault();
    const rect = bar.getBoundingClientRect();
    drag = { ox: ev.clientX, oy: ev.clientY, sl: rect.left, st: rect.top };
    dragHandle.setPointerCapture(ev.pointerId);
  };
  dragHandle.onpointermove = (ev) => {
    if (!drag) return;
    const left = Math.max(0, Math.min(window.innerWidth - 80, drag.sl + (ev.clientX - drag.ox)));
    const top = Math.max(0, Math.min(window.innerHeight - 40, drag.st + (ev.clientY - drag.oy)));
    bar.style.left = `${left}px`;
    bar.style.top = `${top}px`;
    bar.style.transform = "none";
  };
  dragHandle.onpointerup = () => {
    if (!drag) return;
    drag = null;
    try {
      localStorage.setItem(
        "themeCosmeticPreviewChromePos",
        JSON.stringify({ left: parseFloat(bar.style.left) || 0, top: parseFloat(bar.style.top) || 0 }),
      );
    } catch {
      /* ignore */
    }
  };

  const back = document.createElement("button");
  back.type = "button";
  back.className = "btn green";
  back.textContent = "Back to Editor";
  back.onclick = () => opts.onBack();
  bar.appendChild(back);

  document.body.appendChild(bar);
  document.body.classList.add("theme-preview-mode");
  document.body.classList.add("theme-cosmetic-preview-mode");

  return {
    destroy: () => {
      bar.remove();
      document.body.classList.remove("theme-cosmetic-preview-mode");
      if (!document.getElementById("themePreviewChrome")) {
        document.body.classList.remove("theme-preview-mode");
      }
    },
  };
}
